import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import absoluteUrl from "next-absolute-url";

import type { CustomAppContext } from "types/request";
import type { ExpandedUserMeResponse } from "types/users/me";

type authContextType = {
  user: ExpandedUserMeResponse | null;
  loginUrl: string;
  logoutUrl: string;
  login: () => void;
};

type Props = {
  children: ReactNode;
  loginUrl: string;
  logoutUrl: string;
  userData?: ExpandedUserMeResponse;
};

const authContextDefaultValues: authContextType = {
  user: null,
  loginUrl: "",
  logoutUrl: "",
  login: () => {
    // Do nothing
  },
};

export const DOMAINS = JSON.parse(`${process.env.DOMAINS}`) as Record<
  string,
  string
>;
const AuthContext = createContext<authContextType>(authContextDefaultValues);

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({
  children,
  loginUrl,
  logoutUrl,
  userData,
}: Props) => {
  const [user] = useState<ExpandedUserMeResponse | null>(userData || null);
  const login = useCallback(() => {
    window.location.assign(loginUrl);
  }, [loginUrl]);

  const value = useMemo(
    () => ({ user, loginUrl, logoutUrl, login }),
    [user, loginUrl, logoutUrl, login],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.defaultProps = {
  userData: null,
};

export const getServerSideLoginUrl = (appContext: CustomAppContext) => {
  // Get the host from the request
  const { host } = absoluteUrl(appContext.ctx.req);

  // Get the current path without query parameters
  const currentPath = appContext.router.asPath.split("?").shift() ?? "/";

  // Create the continueURL to pass to login URL
  const continueUrl = encodeURIComponent(`https://${host}${currentPath}`);

  const { locale } = appContext.router;

  const baseUrl = DOMAINS[locale ?? "es-AR"];

  // Generate final login URL
  return `https://${baseUrl}/auth/login?nextUrl=${continueUrl}`;
};

export const getServerSideLogoutUrl = (appContext: CustomAppContext) => {
  // Get the base URL from the locale
  const { locale } = appContext.router;
  const baseUrl = DOMAINS[locale ?? "es-AR"];

  // Generate final logout URL
  return `https://${baseUrl}/auth/logout`;
};
